import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import LanguageToggle from "../components/LanguageToggle";
import { useLang } from "../context/LanguageContext";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="flex items-center justify-between mb-8">
        <Logo size={36} />
        <LanguageToggle />
      </div>
      <h1 className="font-display font-bold text-3xl tracking-tight text-u-ink">{t.auth.loginTitle}</h1>
      <p className="text-slate-500 mt-2">{t.auth.loginSubtitle}</p>

      <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
        <Field label={t.auth.email} type="email" value={email} onChange={setEmail} testid="login-email" />
        <Field label={t.auth.password} type="password" value={password} onChange={setPassword} testid="login-password" />
        {error && (
          <p data-testid="login-error" className="text-sm text-u-red bg-u-red/10 border border-u-red/20 rounded-xl px-4 py-3">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-u-ink px-6 py-3.5 font-semibold text-white hover:bg-u-ink/90 transition-all disabled:opacity-60"
        >
          {loading && <Loader2 size={18} className="animate-spin" />}
          {t.auth.loginBtn}
        </button>
      </form>

      <p className="mt-7 text-center text-sm text-slate-500">
        {t.auth.noAccount}{" "}
        <a href="https://use1id-prd.vercel.app/r/register" data-testid="goto-register" className="text-u-ink font-semibold hover:text-u-purple transition-colors">
          {t.auth.signUp}
        </a>
      </p>
    </AuthLayout>
  );
}

export function AuthLayout({ children }) {
  return (
    <div className="relative min-h-screen flex items-center justify-center px-5 py-16 overflow-hidden bg-u-bg">
      <div className="mesh-glow w-[500px] h-[500px] bg-u-purple/20 -top-32 -left-20" />
      <div className="mesh-glow w-[450px] h-[450px] bg-blue-400/20 bottom-0 right-0" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-[32px] card p-8 sm:p-10"
      >
        {children}
      </motion.div>
    </div>
  );
}

export function Field({ label, type, value, onChange, testid, optional = false }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <input
        type={type}
        required={!optional}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-xl bg-u-bg border border-slate-200 px-4 py-3 text-u-ink placeholder-slate-400 outline-none focus:border-u-purple/60 focus:bg-white focus:ring-2 focus:ring-u-purple/15 transition-all"
      />
    </div>
  );
}
